import api from "../../index";

// Types
interface UserEmail {
  email: string;
  name?: string;
  isAdmin: boolean;
  status: "active" | "suspended" | "deleted";
  smtp: string; // SMTP ID
  quotaUsed: number;
  quotaLimit: number;
  lastSync?: Date;
}

interface UserEmailCreateInput {
  email: string;
  name?: string;
  password: string;
  smtp: string;
  quotaLimit?: number;
}

interface UserEmailUpdateInput {
  name?: string;
  status?: "active" | "suspended" | "deleted";
  smtp?: string;
  quotaLimit?: number;
}

interface PaginationResponse<T> {
  items: T[];
  paginationData: {
    page: number;
    perPage: number;
    totalItems: number;
    totalPages: number;
  };
}

interface MailAlias {
  address: string;
  address_display: string;
  forwards_to: string[];
  permitted_senders: string[];
  required: boolean;
}

interface MailUserPrivileges {
  email: string;
  privileges: string[];
  status: string;
  mailbox: string;
  dbInfo?: {
    _id: string;
    name?: string;
    status: "active" | "suspended" | "deleted";
    quotaUsed: number;
    quotaLimit: number;
    lastSync?: Date;
    aliases: string[];
  };
}

interface MailUsersDomainResponse {
  domain: string;
  users: MailUserPrivileges[];
}

// Update UserEmail interface to include aliases
interface UserEmail {
  email: string;
  name?: string;
  isAdmin: boolean;
  status: "active" | "suspended" | "deleted";
  smtp: string;
  quotaUsed: number;
  quotaLimit: number;
  lastSync?: Date;
  aliases: string[]; // Add aliases array
}

// --------- User Email API --------- //

// Create a user email
export const createUserEmail = async (values: UserEmailCreateInput) => {
  return await api.post<UserEmail>("/user-email", values);
};

// Get all user emails
export const findAllUserEmails = async () => {
  return await api.get<UserEmail[]>("/user-emails");
};

// In your API file (e.g., @api/email/users/userEmail.js)
export const getActiveUserEmails = async () => {
  return await api.get("/user-email/active");
};

// Get all user emails paginated
export const findAllUserEmailsPaginated = async (
  page: number,
  perPage: number,
  searchText?: string
) => {
  const searchParams = new URLSearchParams({
    page: page.toString(),
    perPage: perPage.toString(),
    ...(searchText && { searchText }),
  });

  return await api.get<PaginationResponse<UserEmail>>(
    `/user-emails-paginated?${searchParams.toString()}`
  );
};

// Get a user email by ID
export const findUserEmailById = async (id: string) => {
  return await api.get<UserEmail>(`/user-email/${id}`);
};

// Update a user email by ID
export const updateUserEmailById = async (
  id: string,
  values: UserEmailUpdateInput
) => {
  return await api.put<UserEmail>(`/user-email/${id}`, values);
};

// Delete a user email by ID
export const deleteUserEmailById = async (id: string) => {
  return await api.delete(`/user-email/${id}`);
};

// Sync with Mail-in-a-Box
export const syncWithMailbox = async () => {
  return await api.post<{
    added: number;
    updated: number;
    removed: number;
    errors: Array<{ email: string; error: string }>;
  }>("/user-emails/sync");
};

// --------- Mail Aliases API --------- //

// Get all aliases for a user email
export const getUserEmailAliases = async (userEmailId: string) => {
  return await api.get<MailAlias[]>(`/user-email/${userEmailId}/aliases`);
};

// Add an alias to a user email
export const addUserEmailAlias = async (userEmailId: string, alias: string) => {
  return await api.post<UserEmail>(`/user-email/${userEmailId}/aliases`, {
    alias,
  });
};

// Remove an alias from a user email
export const removeUserEmailAlias = async (
  userEmailId: string,
  alias: string
) => {
  return await api.delete<UserEmail>(
    `/user-email/${userEmailId}/aliases/${encodeURIComponent(alias)}`
  );
};

// Sync aliases with Mail-in-a-Box
export const syncUserEmailAliases = async (userEmailId: string) => {
  return await api.post<UserEmail>(`/user-email/${userEmailId}/aliases/sync`);
};

// --------- Mail Users API --------- //

// Get all mail users across all domains
export const getAllMailUsers = async () => {
  return await api.get<{
    status: string;
    data: MailUsersDomainResponse[];
    metadata: {
      totalDomains: number;
      totalUsers: number;
    };
  }>("/mail-box/users");
};

// Example usage with error handling
export const getMailUsersWithErrorHandling = async () => {
  try {
    const response = await getAllMailUsers();
    return response.data;
  } catch (error) {
    // Type guard for error with response
    if (error && typeof error === "object" && "response" in error) {
      const apiError = error as { response: { data: { message: string } } };
      throw new Error(
        apiError.response.data.message || "Failed to fetch mail users"
      );
    }
    throw error;
  }
};

// Utility function to manage alias operations with error handling
export const manageAlias = {
  add: async (userEmailId: string, alias: string) => {
    try {
      const response = await addUserEmailAlias(userEmailId, alias);
      return response.data;
    } catch (error) {
      if (error && typeof error === "object" && "response" in error) {
        const apiError = error as { response: { data: { message: string } } };
        throw new Error(
          apiError.response.data.message || "Failed to add alias"
        );
      }
      throw error;
    }
  },

  remove: async (userEmailId: string, alias: string) => {
    try {
      const response = await removeUserEmailAlias(userEmailId, alias);
      return response.data;
    } catch (error) {
      if (error && typeof error === "object" && "response" in error) {
        const apiError = error as { response: { data: { message: string } } };
        throw new Error(
          apiError.response.data.message || "Failed to remove alias"
        );
      }
      throw error;
    }
  },

  sync: async (userEmailId: string) => {
    try {
      const response = await syncUserEmailAliases(userEmailId);
      return response.data;
    } catch (error) {
      if (error && typeof error === "object" && "response" in error) {
        const apiError = error as { response: { data: { message: string } } };
        throw new Error(
          apiError.response.data.message || "Failed to sync aliases"
        );
      }
      throw error;
    }
  },

  getAll: async (userEmailId: string) => {
    try {
      const response = await getUserEmailAliases(userEmailId);
      return response.data;
    } catch (error) {
      if (error && typeof error === "object" && "response" in error) {
        const apiError = error as { response: { data: { message: string } } };
        throw new Error(
          apiError.response.data.message || "Failed to fetch aliases"
        );
      }
      throw error;
    }
  },
};
